/**
 * US2/T014–T015 (FR-016 to FR-022, §19). Structure follows the two existing precedents rather than
 * inventing a third: `catalogue/brands/New.tsx` (page shell, back button, error surface — itself
 * copied from `vendor/isomorphic/apps/isomorphic/src/app/shared/ecommerce/category/create-category.tsx`,
 * and `account-settings/personal-info.tsx`'s full-width section stack + `FormFooter`) and
 * `components/orders/delivery-zone-editor.tsx` (the repeatable-row `useFieldArray` pattern, with
 * validation on submit rather than a resolver — a blank scratch row from "Add line" is dropped
 * silently, a partially-filled one is a per-field inline error).
 *
 * ## The reversal-visibility gap (specs/007-inventory has no `GET /goods-receipts/{id}` or list —
 * verified against `InventoryController.cs`)
 *
 * A goods receipt can only be shown back to the person recording it *in the same session*, right
 * after they create it — there is no route to look one up afterwards. So this screen is one page
 * with two states: the form, and — once a receipt is created — a confirmation panel holding that
 * exact `GoodsReceipt` in `useState`, with "Reverse this receipt" right there. There is
 * deliberately no `?receiptId=` reversal-by-id entry point: tasks.md's T015 asks only that reversal
 * be wired with a mandatory reason, and building a second, poorer entry point (an id and nothing
 * else to show beside it) for a flow nothing has asked for yet is exactly the speculative surface
 * area worth not building. Flagged for whoever picks up US5/T024 (movement history) — that screen
 * is where a `sourceId` naturally becomes a link back here.
 *
 * The original's flip to `Reversed` is never in a response body this screen reads (see
 * `goodsReceiptStatusLabel`'s note in `lib/services/warehouse.ts`), so the confirmation panel keeps
 * showing the original receipt's own `status` field untouched and adds a separate, honestly-labelled
 * note that a reversal was recorded in this session — it does not fabricate a re-fetched status.
 */
import { useState } from "react";
import { useOutletContext } from "react-router-dom";
import { useFieldArray, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { PiClipboardTextDuotone as LinesIcon, PiPlusBold as Plus, PiTruckDuotone as DeliveryIcon, PiTrashDuotone as Trash2 } from "react-icons/pi";
import { ActionIcon, Button, Input } from "rizzui";
import FormFooter from "@core/components/form-footer";
import { PageHeader, FormGroup, WidgetCard, ErrorSurface, ReasonDialog } from "@steelyard/theme";
import { DateField } from "../../components/date-field";
import { can } from "../../lib/permissions";
import { formCard, formCardFooter, formSections } from "../../lib/form-layout";
import { SectionTitle } from "../../components/section-title";
import type { AccountSummary, PermissionState } from "../../types";
import {
  recordGoodsReceipt,
  reverseGoodsReceipt,
  formatQty,
  personName,
  skuLabel,
  goodsReceiptStatusLabel,
  type GoodsReceipt,
  type GoodsReceiptLineInput,
} from "../../lib/services/warehouse";
import { SkuPicker } from "../../components/catalog-pickers";
import { parseIsoDate, toIsoDate } from "../../lib/format";
import { useGoBack } from "../../lib/navigation";

/** A GRN has no number of its own: "GRN · supplier · date", from its own fields — never its id. */
const grnLabel = (receipt: GoodsReceipt) => `GRN · ${receipt.supplierName} · ${receipt.grnDate}`;

type LineRow = { skuId: string; unitCode: string; quantity: string; conversionToStocking: string; rate: string };
type ReceiptFormValues = { supplierName: string; grnDate: string; lines: LineRow[] };

const blankLine = (): LineRow => ({ skuId: "", unitCode: "", quantity: "", conversionToStocking: "", rate: "" });
const today = () => toIsoDate(new Date());

/** ORD-03: `SkuPicker` only ever writes a real SKU's id, but this still guards the case where a
 * line is otherwise filled in and the picker was left empty. */
const GUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function errorMessageOf(error: unknown): string | undefined {
  if (!error) return undefined;
  return error instanceof Error ? error.message : String(error);
}

export default function GoodsReceiptPage() {
  const { permissionState } = useOutletContext<{ account: AccountSummary; permissionState: PermissionState }>();
  const goBack = useGoBack("/inventory");
  const [error, setError] = useState<unknown>(null);
  const [receipt, setReceipt] = useState<GoodsReceipt | null>(null);
  const [reversal, setReversal] = useState<GoodsReceipt | null>(null);
  const [reverseOpen, setReverseOpen] = useState(false);
  const [reverseError, setReverseError] = useState<unknown>(null);

  const {
    control,
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    setError: setFieldError,
    clearErrors,
    formState: { errors, isSubmitting },
  } = useForm<ReceiptFormValues>({ defaultValues: { supplierName: "", grnDate: today(), lines: [blankLine()] } });
  const { fields, append, remove } = useFieldArray({ control, name: "lines" });

  if (!can(permissionState, "inventory.administer")) {
    return <ErrorSurface error={{ title: "Not permitted", status: 403 }} action="record a goods receipt" />;
  }

  const submit = handleSubmit(async (values) => {
    clearErrors();
    setError(null);
    let hasError = false;

    const supplierName = values.supplierName.trim();
    if (!supplierName) {
      setFieldError("supplierName", { message: "Required" });
      hasError = true;
    }
    if (!values.grnDate) {
      setFieldError("grnDate", { message: "Required" });
      hasError = true;
    }

    const lines: GoodsReceiptLineInput[] = [];
    values.lines.forEach((row, index) => {
      const skuId = row.skuId.trim();
      const unitCode = row.unitCode.trim();
      const quantityRaw = row.quantity.trim();
      const conversionRaw = row.conversionToStocking.trim();
      const rateRaw = row.rate.trim();
      if (!skuId && !unitCode && !quantityRaw && !conversionRaw && !rateRaw) return; // an unfilled scratch row — dropped silently

      if (!GUID_RE.test(skuId)) {
        setFieldError(`lines.${index}.skuId`, { message: "Choose a SKU" });
        hasError = true;
      }

      const quantity = Number(quantityRaw);
      if (!quantityRaw || !Number.isFinite(quantity) || quantity <= 0) {
        setFieldError(`lines.${index}.quantity`, { message: "Must be above zero" });
        hasError = true;
      }

      let conversionToStocking: number | null = null;
      if (conversionRaw) {
        conversionToStocking = Number(conversionRaw);
        if (!Number.isFinite(conversionToStocking) || conversionToStocking <= 0) {
          setFieldError(`lines.${index}.conversionToStocking`, { message: "Must be above zero" });
          hasError = true;
        }
      }

      let rate: number | null = null;
      if (rateRaw) {
        rate = Number(rateRaw);
        if (!Number.isFinite(rate) || rate < 0) {
          setFieldError(`lines.${index}.rate`, { message: "Enter a valid rate" });
          hasError = true;
        }
      }

      lines.push({ skuId, unitCode: unitCode || null, quantity, conversionToStocking, rate });
    });

    if (lines.length === 0) {
      setError(new Error("A goods receipt with no lines records that nothing arrived — add at least one."));
      hasError = true;
    }
    if (hasError) return;

    try {
      const created = await recordGoodsReceipt({ supplierName, grnDate: values.grnDate, lines });
      setReceipt(created);
      setReversal(null);
      toast.success("Goods receipt recorded");
      reset({ supplierName: "", grnDate: today(), lines: [blankLine()] });
    } catch (err) {
      setError(err);
      toast.error("Could not record the goods receipt");
    }
  });

  async function handleReverse(formData: FormData) {
    if (!receipt) return;
    const reason = String(formData.get("reason") ?? "").trim();
    if (!reason) return; // the dialog's textarea is `required`; the browser already refused this
    setReverseError(null);
    try {
      const created = await reverseGoodsReceipt(receipt.id, reason);
      setReversal(created);
      setReverseOpen(false);
      toast.success("Goods receipt reversed");
    } catch (err) {
      setReverseError(err);
      toast.error("Could not reverse the goods receipt");
    }
  }

  return (
    <div className="flex flex-col gap-6 @container">
      <PageHeader
        title="Record A Goods Receipt"
        breadcrumb={[{ name: "Home", href: "/" }, { name: "Stock", href: "/inventory" }, { name: "Goods Receipt" }]}
      />

      {/* FR-019/§4.3: no purchase-order field anywhere on this screen, deliberately (T013). */}
      <p className="text-muted-foreground text-sm">
        Records goods that have arrived. This is not a purchase-order screen — there is no PO to
        raise, approve or reference here.
      </p>

      {receipt ? (
        <WidgetCard title="Recorded receipt" headerClassName="border-muted border-b">
          <div className="flex flex-col gap-4 pt-4">
            <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm">
              <span>
                <span className="text-muted-foreground">Supplier: </span>
                {receipt.supplierName}
              </span>
              <span>
                <span className="text-muted-foreground">GRN date: </span>
                {receipt.grnDate}
              </span>
              <span>
                <span className="text-muted-foreground">Status: </span>
                {goodsReceiptStatusLabel(receipt.status)}
                {reversal ? " — reversed in this session, see below" : ""}
              </span>
              <span>
                <span className="text-muted-foreground">Received by: </span>
                {personName(receipt.receivedByName)}
              </span>
              <span className="text-muted-foreground">{grnLabel(receipt)}</span>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-muted-foreground">
                    <th className="py-1 pr-4 font-medium">SKU</th>
                    <th className="py-1 pr-4 font-medium">Quantity</th>
                    <th className="py-1 pr-4 font-medium">Unit</th>
                    <th className="py-1 pr-4 font-medium">In stocking unit</th>
                    <th className="py-1 pr-4 font-medium">Rate</th>
                  </tr>
                </thead>
                <tbody>
                  {(receipt.lines ?? []).map((line) => (
                    <tr key={line.id} className="border-t border-muted">
                      <td className="py-1.5 pr-4 text-xs">{skuLabel(line)}</td>
                      <td className="py-1.5 pr-4">{formatQty(line.quantity)}</td>
                      <td className="py-1.5 pr-4">{line.unitCode}</td>
                      <td className="py-1.5 pr-4">{formatQty(line.qtyInStockingUnit)}</td>
                      <td className="py-1.5 pr-4">{line.rate === null ? "—" : formatQty(line.rate)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {!reversal ? (
              reverseOpen ? (
                <ReasonDialog
                  open={reverseOpen}
                  title="Reverse this goods receipt"
                  consequence="Records a second, compensating receipt that negates every line of this one. The original is retained, not deleted, and this cannot be undone from this screen (FR-021)."
                  action={handleReverse}
                  confirmLabel="Reverse receipt"
                  cancel={
                    <Button type="button" variant="outline" onClick={() => setReverseOpen(false)}>
                      Cancel
                    </Button>
                  }
                  reasonLabel="Why is this receipt being reversed?"
                  reasonHint="Recorded in the audit log."
                  error={errorMessageOf(reverseError)}
                />
              ) : (
                <Button type="button" variant="outline" className="self-start" onClick={() => setReverseOpen(true)}>
                  Reverse this receipt
                </Button>
              )
            ) : null}
          </div>
        </WidgetCard>
      ) : null}

      {reversal ? (
        <WidgetCard title="Reversal recorded" headerClassName="border-muted border-b">
          <div className="flex flex-col gap-3 pt-4 text-sm">
            <p className="text-muted-foreground">
              A new receipt was recorded reversing every line of the one above. Both are retained —
              nothing was deleted (FR-021, DA-06).
            </p>
            <span className="text-xs text-muted-foreground">
              {grnLabel(reversal)}
              {reversal.reversalOfLabel ? ` — reverses ${reversal.reversalOfLabel}` : ""}
            </span>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-muted-foreground">
                    <th className="py-1 pr-4 font-medium">SKU</th>
                    <th className="py-1 pr-4 font-medium">Quantity</th>
                    <th className="py-1 pr-4 font-medium">Unit</th>
                  </tr>
                </thead>
                <tbody>
                  {(reversal.lines ?? []).map((line) => (
                    <tr key={line.id} className="border-t border-muted">
                      <td className="py-1.5 pr-4 text-xs">{skuLabel(line)}</td>
                      <td className="py-1.5 pr-4">{formatQty(line.quantity)}</td>
                      <td className="py-1.5 pr-4">{line.unitCode}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </WidgetCard>
      ) : null}

      {error ? <ErrorSurface error={error} action="record this goods receipt" /> : null}

      <form noValidate onSubmit={submit} className={formCard}>
        <div className={formSections}>
          <FormGroup title={<SectionTitle icon={DeliveryIcon}>Delivery</SectionTitle>} description="Who it came from and when it arrived. The supplier is a name, not a lookup — supplier records are out of v1.">
              <Input label="Supplier" autoComplete="off" error={errors.supplierName?.message} {...register("supplierName")} />
              <DateField
                required
                dateFormat="dd-MMM-yyyy"
                placeholderText="Choose a date…"
                selected={parseIsoDate(watch("grnDate") || null)}
                onChange={(date) => setValue("grnDate", date ? toIsoDate(date as Date) : "", { shouldValidate: true })}
                inputProps={{
                  label: "GRN date",
                  error: errors.grnDate?.message,
                  clearable: Boolean(watch("grnDate")),
                  onClear: () => setValue("grnDate", "", { shouldValidate: true }),
                }}
              />
          </FormGroup>

          <FormGroup
            title={<SectionTitle icon={LinesIcon}>Lines</SectionTitle>}
            description="One row per SKU. Unit and conversion default to the SKU's stocking unit when left blank; a rate is optional."
          >
            <div className="col-span-full flex flex-col gap-4">
              {fields.map((item, index) => (
                // SKU id takes the spare width; the four numeric columns share the rest evenly.
                <div key={item.id} className="grid grid-cols-2 items-start gap-3 @5xl:grid-cols-[minmax(0,2fr)_repeat(4,minmax(0,1fr))_2.5rem]">
                  <div className="col-span-2 @5xl:col-span-1">
                    <SkuPicker
                      value={watch(`lines.${index}.skuId`)}
                      onChange={(id) => setValue(`lines.${index}.skuId`, id, { shouldValidate: true })}
                      error={errors.lines?.[index]?.skuId?.message}
                      required
                    />
                  </div>
                  <Input
                    label="Unit"
                    placeholder="stocking unit"
                    {...register(`lines.${index}.unitCode`)}
                    error={errors.lines?.[index]?.unitCode?.message}
                  />
                  <Input
                    label="Quantity"
                    inputMode="decimal"
                    {...register(`lines.${index}.quantity`)}
                    error={errors.lines?.[index]?.quantity?.message}
                  />
                  <Input
                    label="Conversion"
                    inputMode="decimal"
                    placeholder="1 (same unit)"
                    {...register(`lines.${index}.conversionToStocking`)}
                    error={errors.lines?.[index]?.conversionToStocking?.message}
                  />
                  <Input
                    label="Rate"
                    inputMode="decimal"
                    placeholder="optional"
                    {...register(`lines.${index}.rate`)}
                    error={errors.lines?.[index]?.rate?.message}
                  />
                  {fields.length > 1 && (
                    <ActionIcon
                      type="button"
                      variant="flat"
                      onClick={() => remove(index)}
                      className="@5xl:mt-7"
                      aria-label={`Remove line ${index + 1}`}
                    >
                      <Trash2 aria-hidden className="size-4" />
                    </ActionIcon>
                  )}
                </div>
              ))}

              <Button type="button" variant="outline" className="self-start" onClick={() => append(blankLine())}>
                <Plus aria-hidden className="me-2 size-4" strokeWidth={2} />
                Add line
              </Button>
            </div>
          </FormGroup>
        </div>

        <FormFooter
          className={formCardFooter}
          submitBtnText={isSubmitting ? "Recording…" : "Record goods receipt"}
          isLoading={isSubmitting}
          altBtnText="Cancel"
          handleAltBtn={goBack}
        />
      </form>
    </div>
  );
}
